import { useState, type FormEvent } from "react"
import { ArrowRight, Tool01 } from "@untitledui/icons"
import { AnimatePresence } from "motion/react"
import { toast } from "sonner"
import { teamsApi } from "@/lib/api/teams"
import { ApiError } from "@/lib/api/client"
import { useOnboarding } from "@/lib/onboarding/onboarding-context"
import {
  Field,
  PrimaryButton,
  StaggerField,
  StepActions,
  StepLayout,
} from "./onboarding-shell"
import { OnboardingContextPanel, GroupNetworkTeaching } from "./context-panel"
import {
  AddRowButton,
  TeamRowCard,
  makeBodyshopRow,
  validateRowFields,
  type TeamRow,
} from "./team-row-card"
import { cx } from "@/lib/utils/cx"

// Step 5 (group). Body shops. Same per-row commit model as the dealerships
// step:
//   1. Fill the row (name + city + state required; GM invite is opt-in via
//      the "+ Invite the GM" toggle inside TeamRowCard).
//   2. Click "Add body shop" (or "Send invite & add body shop" when the
//      invite section is open). The shop is created as a standalone team,
//      the invite is fired if applicable, and the shop is connected to
//      every dealership currently checked in its "Routes to" chip group.
//   3. Row collapses into a sent confirmation.
//
// The whole step is fully skippable — there's a "Skip — add body shops
// later" CTA. Skipping leaves the dashboard's connect-bodyshop warning to
// nudge the user back into Settings later.

interface BodyshopRow extends TeamRow {
  // Dealership team ids this shop should connect to. Initialised to ALL
  // dealerships from step 4 so the common case is a single click. Toggled
  // via the per-row chip group rendered as TeamRowCard's `extra` slot.
  routesTo: Set<number>
}

let bodyshopExtraKey = 1

export function GroupBodyshopsStep() {
  const { goBack, finish, createdDealerships } = useOnboarding()

  const seedRoutes = new Set(createdDealerships.map((d) => d.teamId))
  const makeRow = (): BodyshopRow => ({
    ...makeBodyshopRow(),
    key: -bodyshopExtraKey++,
    routesTo: new Set(seedRoutes),
  })

  const [rows, setRows] = useState<BodyshopRow[]>(() => [makeRow()])
  // Body-shop GM is optional. Leave the email blank to skip the invite —
  // no separate toolbar required. The user can also Skip the whole step
  // via the StepActions skip button.
  const inviteMode = "optional" as const

  const sentRows = rows.filter((r) => r.status === "sent")
  const hasDraftRow = rows.some((r) => r.status === "draft")

  const updateRow = (key: number, patch: Partial<TeamRow>) => {
    setRows((rs) =>
      rs.map((r) => (r.key === key ? { ...r, ...patch, errors: undefined } : r)),
    )
  }
  const setRowState = (key: number, patch: Partial<TeamRow>) => {
    setRows((rs) => rs.map((r) => (r.key === key ? { ...r, ...patch } : r)))
  }

  const toggleRoute = (rowKey: number, dealershipTeamId: number) => {
    setRows((rs) =>
      rs.map((r) => {
        if (r.key !== rowKey) return r
        const next = new Set(r.routesTo)
        if (next.has(dealershipTeamId)) next.delete(dealershipTeamId)
        else next.add(dealershipTeamId)
        return { ...r, routesTo: next }
      }),
    )
  }
  const setAllRoutes = (rowKey: number) => {
    setRows((rs) =>
      rs.map((r) =>
        r.key === rowKey
          ? { ...r, routesTo: new Set(createdDealerships.map((d) => d.teamId)) }
          : r,
      ),
    )
  }
  const clearAllRoutes = (rowKey: number) => {
    setRows((rs) =>
      rs.map((r) => (r.key === rowKey ? { ...r, routesTo: new Set<number>() } : r)),
    )
  }

  const addRow = () => setRows((rs) => [...rs, makeRow()])
  const removeRow = (key: number) => setRows((rs) => rs.filter((r) => r.key !== key))

  // Per-row send. Validation: name + city + state always required; invite
  // fields are optional but validated if the user typed an email. The
  // "Routes to" links are also optional — the user may skip linking and
  // just create the shop, which is the equivalent of "I'll wire this up
  // later in Settings."
  const handleSendRow = async (key: number) => {
    const current = rows.find((r) => r.key === key)
    if (!current) return
    const { row: validated, ok, willInvite } = validateRowFields(
      current,
      inviteMode,
    )
    setRows((rs) =>
      rs.map((r) =>
        r.key === key ? ({ ...validated, routesTo: current.routesTo } as BodyshopRow) : r,
      ),
    )
    if (!ok) return

    setRowState(key, { status: "sending" })
    try {
      // BE's connectBodyshop links by name — it queues a welcome email
      // when the bodyshop is brand new. We create the team explicitly
      // first (POST /teams) so we own its id for downstream cache
      // updates, then link each routed-to dealership by name. The first
      // link finds the just-created team and sends the welcome email;
      // subsequent links to the same name reuse the team without a
      // second email.
      const teamName = current.name.trim()
      const team = await teamsApi.create({
        name: teamName,
        type: "Bodyshop",
        city: current.city.trim(),
        state: current.state.trim(),
      })
      if (willInvite) {
        await teamsApi.invite(team.id, {
          email: current.gmEmail.trim(),
          role: current.gmRole,
        })
      }
      for (const dealershipTeamId of current.routesTo) {
        await teamsApi.connectBodyshop(dealershipTeamId, {
          bodyshopName: teamName,
        })
      }
      setRowState(key, { status: "sent", createdTeamId: team.id })
      const linked = current.routesTo.size
      toast.success(
        `${current.name.trim()} added${willInvite ? " · invite sent" : ""}${
          linked > 0 ? ` · linked to ${linked} dealership${linked === 1 ? "" : "s"}` : ""
        }`,
      )
    } catch (err) {
      const msg =
        err instanceof ApiError ? err.message : "Couldn't add this body shop"
      toast.error(msg)
      setRowState(key, { status: "draft" })
    }
  }

  const handleContinue = async (e: FormEvent) => {
    e.preventDefault()
    await finish()
  }
  const handleSkip = async () => {
    await finish()
  }

  const totalRoutes = sentRows.reduce(
    (acc, r) => acc + (r as BodyshopRow).routesTo.size,
    0,
  )

  return (
    <StepLayout
      eyebrow="Step 5 — Body shops"
      title="Add the shops your dealerships send work to"
      subtitle="A body shop is its own team — the people who handle the actual repairs. You can add zero, one, or many, and connect each shop to one or more dealerships. Skip and you'll get a reminder on the dashboard."
      hero={
        <OnboardingContextPanel
          teaching={
            <GroupNetworkTeaching
              dealerships={createdDealerships.map((d) => ({
                name: d.name,
                city: d.city,
                state: d.state,
              }))}
              bodyshops={sentRows.map((r) => ({
                name: r.name,
                city: r.city,
                state: r.state,
              }))}
              connectionCount={totalRoutes}
            />
          }
        />
      }
    >
      <form onSubmit={handleContinue} className="space-y-6">
        <StaggerField index={0}>
          <Field
            label="Body shops"
            hint={
              createdDealerships.length === 1
                ? `By default each shop connects to ${createdDealerships[0].name} — you can clear the link.`
                : `By default each shop connects to all ${createdDealerships.length} of your dealerships — you can pick a subset per shop.`
            }
          >
            <div className="space-y-2.5">
              <AnimatePresence initial={false}>
                {rows.map((r, i) => {
                  const sentSummary =
                    r.status === "sent"
                      ? `Routes to: ${
                          createdDealerships
                            .filter((d) => r.routesTo.has(d.teamId))
                            .map((d) => d.name)
                            .join(", ") || "—"
                        }`
                      : undefined
                  return (
                    <TeamRowCard
                      key={r.key}
                      row={r}
                      index={i}
                      isPrimary={false}
                      canRemove={rows.length > 1 || r.status === "sent"}
                      palette="amber"
                      placeholderName={`Body shop ${i + 1} (e.g. LaFontaine Collision)`}
                      inviteMode={inviteMode}
                      sendLabel={
                        r.gmEmail.trim()
                          ? "Add body shop & invite GM"
                          : "Add body shop"
                      }
                      sentSummary={sentSummary}
                      onChange={(patch) => updateRow(r.key, patch)}
                      onSend={() => handleSendRow(r.key)}
                      onRemove={() => removeRow(r.key)}
                      extra={
                        r.status === "draft" ? (
                          <RoutesToChips
                            dealerships={createdDealerships}
                            selected={r.routesTo}
                            onToggle={(teamId) => toggleRoute(r.key, teamId)}
                            onSetAll={() => setAllRoutes(r.key)}
                            onClearAll={() => clearAllRoutes(r.key)}
                          />
                        ) : null
                      }
                    />
                  )
                })}
              </AnimatePresence>
              {!hasDraftRow && (
                <AddRowButton
                  onClick={addRow}
                  label="+ Add another body shop"
                  palette="amber"
                />
              )}
            </div>
          </Field>
        </StaggerField>

        <StaggerField index={1}>
          <StepActions
            onBack={goBack}
            skip={{
              onSkip: handleSkip,
              label: "Skip for now",
            }}
            primary={
              <PrimaryButton variant="emerald">
                {sentRows.length === 0 ? "Finish" : `Finish · ${sentRows.length} added`}
                <ArrowRight className="size-[18px]" aria-hidden />
              </PrimaryButton>
            }
          />
        </StaggerField>
      </form>
    </StepLayout>
  )
}

function RoutesToChips({
  dealerships,
  selected,
  onToggle,
  onSetAll,
  onClearAll,
}: {
  dealerships: { teamId: number; name: string; city: string; state: string }[]
  selected: Set<number>
  onToggle: (teamId: number) => void
  onSetAll: () => void
  onClearAll: () => void
}) {
  if (dealerships.length === 0) return null
  const allSelected = selected.size === dealerships.length
  const noneSelected = selected.size === 0
  return (
    <div className="mt-4 rounded-xl border border-zinc-200 bg-white p-3 space-y-3">
      <div className="flex items-center justify-between gap-2">
        <div>
          <div className="flex items-center gap-1.5">
            <span className="text-[12px] font-semibold text-zinc-700">
              Connect to dealerships
            </span>
            <span className="text-[10px] font-bold tracking-wide text-zinc-400">
              OPTIONAL
            </span>
          </div>
          <p className="text-[11.5px] text-zinc-500 mt-0.5">
            Pick which dealerships send work to this shop.
          </p>
        </div>
        <div className="flex items-center gap-1 shrink-0">
          <button
            type="button"
            onClick={onSetAll}
            disabled={allSelected}
            className="h-7 px-2.5 rounded-lg text-[11.5px] font-semibold text-zinc-600 border border-zinc-200 hover:border-zinc-300 hover:bg-zinc-50 disabled:opacity-40 disabled:hover:bg-white disabled:hover:border-zinc-200 transition-all duration-150"
          >
            Select all
          </button>
          <button
            type="button"
            onClick={onClearAll}
            disabled={noneSelected}
            className="h-7 px-2.5 rounded-lg text-[11.5px] font-semibold text-zinc-600 border border-zinc-200 hover:border-zinc-300 hover:bg-zinc-50 disabled:opacity-40 disabled:hover:bg-white disabled:hover:border-zinc-200 transition-all duration-150"
          >
            Clear
          </button>
        </div>
      </div>
      <div className="space-y-1">
        {dealerships.map((d) => {
          const isSelected = selected.has(d.teamId)
          const cityState = [d.city, d.state].filter(Boolean).join(", ")
          return (
            <button
              key={d.teamId}
              type="button"
              onClick={() => onToggle(d.teamId)}
              className={cx(
                "w-full flex items-center gap-3 p-2 rounded-lg text-left transition-all duration-150",
                isSelected
                  ? "bg-emerald-50/70 hover:bg-emerald-50"
                  : "bg-white hover:bg-zinc-50",
              )}
            >
              <span
                className={cx(
                  "size-5 rounded-md border-2 grid place-items-center shrink-0 transition-all duration-150",
                  isSelected
                    ? "bg-emerald-500 border-emerald-500"
                    : "bg-white border-zinc-300",
                )}
                aria-hidden
              >
                {isSelected && (
                  <svg viewBox="0 0 12 12" className="size-3 text-white">
                    <path
                      d="M2.5 6.5L5 9l4.5-5"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                )}
              </span>
              <div className="min-w-0 flex-1">
                <p
                  className={cx(
                    "text-[13.5px] font-semibold truncate",
                    isSelected ? "text-emerald-900" : "text-zinc-900",
                  )}
                >
                  {d.name}
                </p>
                {cityState && (
                  <p
                    className={cx(
                      "text-[11.5px] truncate",
                      isSelected ? "text-emerald-700/70" : "text-zinc-400",
                    )}
                  >
                    {cityState}
                  </p>
                )}
              </div>
            </button>
          )
        })}
      </div>
      {noneSelected && (
        <p className="text-[11.5px] text-zinc-500 px-1">
          No dealerships linked. The shop will be created on its own — you
          can wire connections later in Settings.
        </p>
      )}
    </div>
  )
}
